import { useEffect } from "react";
import { useReferralData } from "./hooks/use-referral-data";
import { useAffiliateData } from "../affiliate/hooks/use-affiliate-data";
import { useNavigate } from "react-router-dom";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { AuthGate } from "@/components/auth/auth-gate";
import { getMockReferralsData } from "@/lib/mock-data";

export const ReferralsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useUnifiedAuth();
  const { affiliate, isLoading: isLoadingAffiliate } = useAffiliateData();
  
  // If user is an active affiliate, redirect to affiliate page immediately
  useEffect(() => {
    if (affiliate && (affiliate.status === "active" || affiliate.status === "pending")) {
      navigate("/affiliate");
    }
  }, [affiliate, navigate]);

  // Loading affiliate status to check if user should be redirected
  if (isLoadingAffiliate && isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 w-64 bg-muted rounded mb-4" />
          <div className="h-32 bg-muted rounded" />
        </div>
      </div>
    );
  }

  // Don't render referrals page for affiliates (they'll be redirected)
  if (affiliate && (affiliate.status === "active" || affiliate.status === "pending")) {
    return null;
  }

  // Wrap content with AuthGate for preview
  return (
    <AuthGate
      title="Referrals"
      description="Earn 10% of trading fees. Connect to view your referrals and track earnings in real-time."
    >
      <ReferralsPageContent />
    </AuthGate>
  );
};

// Separate component for referral page content that only loads when user is not an affiliate
const ReferralsPageContent = () => {
  const { isAuthenticated } = useUnifiedAuth();
  const realData = useReferralData();
  
  // Use mock data for preview when not authenticated
  const mockData = getMockReferralsData();
  const {
    code,
    stats,
    referrals,
    isLoading,
  } = isAuthenticated 
    ? realData 
    : {
        code: mockData.code,
        stats: mockData.stats,
        referrals: mockData.referrals,
        isLoading: false,
      };

  if (isLoading && isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 w-64 bg-muted rounded mb-4" />
          <div className="h-32 bg-muted rounded" />
        </div>
      </div>
    );
  }

  // Use custom_code if available, otherwise use ref_code, with fallback
  const referralCode = code?.custom_code || code?.ref_code || "LOADING";
  const referralUrl = `${window.location.origin}?ref=${referralCode}`;

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Referrals</h1>
        <p className="text-muted-foreground">
          Refer traders, earn 10% of their trading fees, and they get 10% off
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="rounded-xl bg-card p-6 border border-border/50">
          <p className="text-sm text-muted-foreground mb-1">Total Referrals</p>
          <p className="text-3xl font-bold">{stats?.total_referrals || 0}</p>
        </div>
        <div className="rounded-xl bg-card p-6 border border-border/50">
          <p className="text-sm text-muted-foreground mb-1">Total Earnings</p>
          <p className="text-3xl font-bold text-emerald-500">
            ${parseFloat(stats?.total_earnings || "0").toFixed(2)}
          </p>
        </div>
        <div className="rounded-xl bg-card p-6 border border-border/50">
          <p className="text-sm text-muted-foreground mb-1">Claimable</p>
          <p className="text-3xl font-bold text-primary">
            ${parseFloat(stats?.claimable_balance || "0").toFixed(2)}
          </p>
        </div>
        <div className="rounded-xl bg-card p-6 border border-border/50">
          <p className="text-sm text-muted-foreground mb-1">Total Volume</p>
          <p className="text-3xl font-bold">
            ${parseFloat(stats?.total_volume_usd || "0").toLocaleString()}
          </p>
        </div>
      </div>

      {/* Referral Code Card */}
      <div className="rounded-xl bg-card p-6 border border-border/50 mb-8">
        <h2 className="text-xl font-semibold mb-4">Your Referral Link</h2>
        <div className="flex gap-2">
          <input
            type="text"
            value={referralUrl}
            readOnly
            className="flex-1 px-4 py-2 rounded-lg bg-muted border border-border/50 font-mono text-sm"
          />
          <button
            onClick={() => {
              navigator.clipboard.writeText(referralUrl);
            }}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
          >
            Copy
          </button>
        </div>
        <p className="text-sm text-muted-foreground mt-3">
          Share this link with traders. You earn 10% of their fees (up to $100M volume per referral). They save 10% on fees (up to $10M volume).
        </p>
      </div>

      {/* Referrals Table */}
      <div className="rounded-xl bg-card border border-border/50 overflow-hidden">
        <div className="p-6 border-b border-border/50">
          <h2 className="text-xl font-semibold">Your Referrals ({referrals.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Joined
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Volume
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Your Earnings
                </th>
              </tr>
            </thead>
            <tbody>
              {referrals.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-muted-foreground">
                    No referrals yet. Share your link to get started!
                  </td>
                </tr>
              ) : (
                referrals.map((ref) => (
                  <tr key={ref.id} className="border-t border-border/30 hover:bg-muted/20 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-xs font-medium">
                          {(ref.email || ref.wallet_address || "?")[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {ref.email || `${ref.wallet_address?.slice(0, 6)}...${ref.wallet_address?.slice(-4)}`}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {new Date(ref.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      ${parseFloat(ref.total_volume_usd).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-emerald-500">
                      +${parseFloat(ref.total_commissions).toFixed(2)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Claim Button */}
      {parseFloat(stats?.claimable_balance || "0") >= 5 && (
        <div className="mt-8 flex justify-center">
          <button className="px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold text-lg">
            Claim ${parseFloat(stats?.claimable_balance || "0").toFixed(2)} to Trading Balance
          </button>
        </div>
      )}
    </div>
  );
};

